import { ApiError, activateEvent, confirmEvent, getEvent, updateEvent } from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { EventResponse, UpdateEventRequest } from '@jq4race/shared';
import {
  Badge,
  buttonVariants,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@jq4race/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { CloseEventAction } from '../components/CloseEventAction';
import { EventForm } from '../components/EventForm';
import { EventReportSection } from '../components/EventReportSection';
import { EventTariffSection } from '../components/EventTariffSection';
import { GuestListSection } from '../components/GuestListSection';
import { StaffingSection } from '../components/StaffingSection';
import { EVENT_STATUS_TONE, isFieldEditable } from '../constants';

export function EditEventPage() {
  const { t } = useTranslation('events');
  const { eventId } = useParams({ strict: false });
  const navigate = useNavigate();

  const [event, setEvent] = useState<EventResponse | null>(null);
  const [loading, setLoading] = useState(true);
  // Page-level error (event failed to load) — gates the ENTIRE page render.
  const [loadError, setLoadError] = useState<string | null>(null);
  // Transition-level error (confirm/activate 409 etc) — surfaced as a
  // non-destructive inline banner so the loaded form stays intact and the
  // user can retry without losing their place.
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const [tab, setTab] = useState<'details' | 'guests' | 'staffing' | 'tariff' | 'report'>(
    'details',
  );

  const loadEvent = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const result = await getEvent(eventId);
      setEvent(result);
    } catch (e) {
      setLoadError(translateApiError(e, t, { fallback: t('editEventPage.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [eventId, t]);

  useEffect(() => {
    void loadEvent();
  }, [loadEvent]);

  async function handleSubmit(values: UpdateEventRequest) {
    if (!eventId || !event) return;
    try {
      // R1.4 field-editability: react-hook-form's handleSubmit always returns
      // the FULL form values, including frozen (disabled) fields — the plain
      // HTML `disabled` attribute does not exclude a field from submission.
      // The backend does a presence-based check (`field !== undefined` →
      // must pass canEditField), so sending an unchanged-but-frozen field
      // (e.g. locationId while `scheduled`) 409s. Only forward fields that
      // are actually editable for the event's current status — the backend
      // schema is `.partial()`, so a partial payload is valid.
      const payload: UpdateEventRequest = {};
      if (isFieldEditable(event.status, 'name')) payload.name = values.name;
      if (isFieldEditable(event.status, 'locationId')) payload.locationId = values.locationId;
      if (isFieldEditable(event.status, 'scheduledDate')) {
        payload.scheduledDate = values.scheduledDate;
      }
      if (isFieldEditable(event.status, 'scheduledTime')) {
        payload.scheduledTime = values.scheduledTime;
      }
      if (isFieldEditable(event.status, 'expectedCapacity')) {
        payload.expectedCapacity = values.expectedCapacity;
      }
      if (isFieldEditable(event.status, 'chargeMode')) payload.chargeMode = values.chargeMode;
      if (isFieldEditable(event.status, 'photoPolicy')) payload.photoPolicy = values.photoPolicy;

      await updateEvent(eventId, payload);
      void navigate({ to: '/dashboard/events' });
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(
          translateApiError(e, t, { fallback: t('editEventPage.updateError') }),
        );
      }
      throw e;
    }
  }

  async function handleConfirm() {
    if (!event) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      const updated = await confirmEvent(event.id);
      setEvent(updated);
    } catch (e) {
      setTransitionError(translateApiError(e, t, { fallback: t('editEventPage.confirmError') }));
    } finally {
      setTransitioning(false);
    }
  }

  async function handleActivate() {
    if (!event) return;
    setTransitioning(true);
    setTransitionError(null);
    try {
      const updated = await activateEvent(event.id);
      setEvent(updated);
    } catch (e) {
      setTransitionError(translateApiError(e, t, { fallback: t('editEventPage.activateError') }));
    } finally {
      setTransitioning(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (loadError || !event) {
    return (
      <div className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger">
        {loadError ?? t('editEventPage.notFound')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('editEventPage.eyebrow')}
        title={t('editEventPage.title', { name: event.name })}
        description={t('editEventPage.description')}
        meta={
          <Badge variant={EVENT_STATUS_TONE[event.status]} className="capitalize">
            {t(`constants.status.${event.status}`)}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            {event.status === 'draft' && (
              <button
                type="button"
                className={buttonVariants({ size: 'sm', variant: 'outline' })}
                onClick={() => void handleConfirm()}
                disabled={transitioning}
              >
                {transitioning ? t('editEventPage.confirming') : t('editEventPage.confirm')}
              </button>
            )}
            {event.status === 'scheduled' && (
              <button
                type="button"
                className={buttonVariants({ size: 'sm', variant: 'outline' })}
                onClick={() => void handleActivate()}
                disabled={transitioning}
              >
                {transitioning ? t('editEventPage.activating') : t('editEventPage.activate')}
              </button>
            )}
            {event.status === 'active' && (
              <CloseEventAction
                eventId={event.id}
                onClosed={(updated) => setEvent(updated)}
                onError={(message) => setTransitionError(message)}
              />
            )}
            <Link
              to="/dashboard/events"
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              {t('editEventPage.backToList')}
            </Link>
          </div>
        }
      />

      <FeedbackBanner message={transitionError} onDismiss={() => setTransitionError(null)} />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)}>
        <TabsList>
          <TabsTrigger value="details">{t('editEventPage.tabs.details')}</TabsTrigger>
          <TabsTrigger value="guests">{t('editEventPage.tabs.guests')}</TabsTrigger>
          <TabsTrigger value="staffing">{t('editEventPage.tabs.staffing')}</TabsTrigger>
          {event.chargeMode === 'per_attendee' && (
            <TabsTrigger value="tariff">{t('editEventPage.tabs.tariff')}</TabsTrigger>
          )}
          {event.status === 'closed' && (
            <TabsTrigger value="report">{t('editEventPage.tabs.report')}</TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="details">
          <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
            <CardContent className="p-6 sm:p-8">
              <EventForm
                mode="edit"
                status={event.status}
                defaultValues={{
                  name: event.name,
                  locationId: event.locationId,
                  scheduledDate: event.scheduledDate,
                  scheduledTime: event.scheduledTime,
                  expectedCapacity: event.expectedCapacity,
                  chargeMode: event.chargeMode,
                  photoPolicy: event.photoPolicy,
                }}
                onSubmit={handleSubmit}
                submitLabel={t('editEventPage.saveChanges')}
                onCancel={() => void navigate({ to: '/dashboard/events' })}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="guests">
          <GuestListSection eventId={event.id} eventStatus={event.status} />
        </TabsContent>

        <TabsContent value="staffing">
          <StaffingSection eventId={event.id} eventStatus={event.status} />
        </TabsContent>

        {event.chargeMode === 'per_attendee' && (
          <TabsContent value="tariff">
            <EventTariffSection eventId={event.id} />
          </TabsContent>
        )}

        {event.status === 'closed' && (
          <TabsContent value="report">
            <EventReportSection eventId={event.id} currency={event.currency} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────
// Mirrors EventsPage's inline (non-destructive) error banner — a
// transition-error must never replace the loaded form.

interface FeedbackBannerProps {
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('events');
  if (!message) return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger"
    >
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
